import { getRemoHosts } from "config";
import { useI18n } from "i18n";
import useCompanySettings from "modules/company/hooks/useCompanySettings";
import { CompanyPublicSettings, ICompany } from "modules/company/types";
import {
  selectCurrentEventCompany,
  selectCurrentEventCompanyPlan,
} from "modules/event/selectors";
import { stopListeningEventUnload } from "modules/event/utils/eventUtils";
import { addErrorNotification } from "modules/notification/redux/notificationSlice";
import { isCustomDomainEnabled } from "modules/settings/utils/isCustomDomainEnabled";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";

export const REDIRECT_TO_CUSTOM_DOMAIN_PATHNAMES = [
  "/e",
  "/c",
  "/s",
  "/a",
  "/h",
  "/mic-cam-check",
  "/event-invite",
  // login-callback and other callback URLs would belong here, but it's guaranteed to be using the correct domain
];

export const REDIRECT_TO_MAIN_DOMAIN_PATHNAMES = [
  "/get-started",
  "/create-company",
  "/start-free-trial",
];

// other paths like geartest should work regardless of domain, and keep it there instead of redirecting

export const CustomDomainHandler = () => {
  const { t } = useI18n();
  const location = useLocation();
  const { getCompanyPublicSettings } = useCompanySettings();
  const eventCompany = useSelector(selectCurrentEventCompany);
  const eventCompanyPlan = useSelector(selectCurrentEventCompanyPlan);
  const [eventCompanySettings, setEventCompanySettings] =
    useState<CompanyPublicSettings | null>(null);

  useEffect(() => {
    const start = async () => {
      const companyId =
        typeof eventCompany === "string"
          ? eventCompany
          : (eventCompany as ICompany).id;

      try {
        const result = await getCompanyPublicSettings(companyId);

        setEventCompanySettings(result);
      } catch (error) {
        addErrorNotification({
          message: t("common:error.unknown"),
        });
      }
    };

    if (eventCompany) {
      start();
    } else {
      setEventCompanySettings(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventCompany]);

  useEffect(() => {
    // check if location path is in REDIRECT_TO_CUSTOM_DOMAIN_PATHNAMES
    if (
      REDIRECT_TO_CUSTOM_DOMAIN_PATHNAMES.includes(location.pathname) ||
      REDIRECT_TO_CUSTOM_DOMAIN_PATHNAMES.some((pathname) =>
        location.pathname.startsWith(`${pathname}/`),
      )
    ) {
      if (!eventCompany || !eventCompanyPlan || !eventCompanySettings) {
        return;
      }

      // redirect to custom domain
      if (
        isCustomDomainEnabled(eventCompanySettings, eventCompanyPlan) &&
        window.location.host !== eventCompanySettings.customDomain
      ) {
        stopListeningEventUnload();
        window.location.href = `https://${eventCompanySettings.customDomain}${location.pathname}${location.search}`;
      }
    }

    if (
      (REDIRECT_TO_MAIN_DOMAIN_PATHNAMES.includes(location.pathname) ||
        REDIRECT_TO_MAIN_DOMAIN_PATHNAMES.some((pathname) =>
          location.pathname.startsWith(`${pathname}/`),
        )) &&
      !getRemoHosts().includes(window.location.host)
    ) {
      const protocol = getRemoHosts().includes("localhost:3000")
        ? "http"
        : "https";
      window.location.href = `${protocol}://${getRemoHosts().find((host) =>
        host.includes("remo"),
      )}${location.pathname}${location.search}`;
    }
  }, [
    eventCompany,
    eventCompanyPlan,
    eventCompanySettings,
    location.pathname,
    location.search,
  ]);

  return null;
};
